import { buildAtlAttributionHeaders } from '@atlassian/search-client/atl-attribution';
import {
	type PositionEmploymentType,
	positionEmploymentTypesQuery,
} from '@atlassian/search-client/positionEmploymentType';

import { type CloudConfig } from '../../../../../common/constants/filters/cloud-config/types';
import {
	type FetchedSelectFilterOption,
	type SelectFilterState,
} from '../../../../../common/constants/filters/select-filter/types';
import { ProductKeys, type ProductKeys1P } from '../../../../../common/constants/product-keys';
import { createMultiSelectFilter } from '../../../../filters/multi-select-filter';

// Number of results returned in dropdown
const LIMIT = 20;

const EmploymentTypeMapping: Record<string, string> = {
	Contingent: 'contingent',
	Employee: 'employee',
	Contractor: 'contractor',
};

const ReverseEmploymentTypeMapping: Record<string, string> = Object.fromEntries(
	Object.entries(EmploymentTypeMapping).map(([key, value]) => [value, key]),
);

export const mapPositionEmploymentTypeResult: (
	positionEmploymentType: PositionEmploymentType,
	product: ProductKeys,
) => FetchedSelectFilterOption = (
	positionEmploymentType: PositionEmploymentType,
	product: ProductKeys,
): FetchedSelectFilterOption => {
	const value =
		EmploymentTypeMapping[positionEmploymentType.node.stringValue || ''] || 'unrecognized';
	return {
		trackingKey: value,
		label: positionEmploymentType.node.stringValue || '',
		value: value,
		queryParamValue: value,
		product: product,
	};
};

/**
 * Queries position employment types by search term
 * @param query
 * @param config
 * @returns
 */
const searchPositionEmploymentTypes = async (
	query: string,
	config: CloudConfig,
): Promise<FetchedSelectFilterOption[]> => {
	const rqlQuery = `employmentType ~ '${query}'`;
	const headers = buildAtlAttributionHeaders({
		cloudId: config.cloudId,
		activationId: config.activationId,
		product: config.primaryProduct,
	});
	const positionEmploymentTypesResponse = await positionEmploymentTypesQuery(
		config.cloudId || '',
		rqlQuery,
		LIMIT,
		undefined,
		headers,
	);
	const positionEmploymentTypes: PositionEmploymentType[] =
		positionEmploymentTypesResponse.data?.radar_fieldValues.edges ?? [];

	return positionEmploymentTypes.map((positionEmploymentType) =>
		mapPositionEmploymentTypeResult(positionEmploymentType, config.product as ProductKeys),
	);
};

/**
 * Queries all selected focus areas by id from query params
 * @param queryParams
 * @param config
 * @returns
 */
const getOptionsWithSelectionsFromQueryParams = async (
	queryParams: string[],
	config: CloudConfig,
): Promise<FetchedSelectFilterOption[]> => {
	const rqlQuery = `employmentType in ('${queryParams
		.map((param) => ReverseEmploymentTypeMapping[param || 'unrecognized'])
		.filter(Boolean)
		.join("', '")}')`;
	const headers = buildAtlAttributionHeaders({
		cloudId: config.cloudId,
		activationId: config.activationId,
		product: config.primaryProduct,
	});
	const positionEmploymentTypesResponse = await positionEmploymentTypesQuery(
		config.cloudId || '',
		rqlQuery,
		LIMIT,
		undefined,
		headers,
	);
	const positionEmploymentTypes: PositionEmploymentType[] =
		positionEmploymentTypesResponse.data?.radar_fieldValues.edges ?? [];

	return positionEmploymentTypes.map((positionEmploymentType) =>
		mapPositionEmploymentTypeResult(positionEmploymentType, config.product as ProductKeys),
	);
};

const productKeys1P: ProductKeys1P[] = [ProductKeys.Talent];

export const filter: Readonly<{
	id: 'positionEmploymentType';
	type: 'single-select' | 'multi-select';
	queryParams: {
		key: string;
		getState: (
			queryParams: Record<string, string[]>,
			cloudId: string,
		) => SelectFilterState<'positionEmploymentType'>;
	};
	products: ProductKeys[];
	universal: boolean;
	customValueFields?: string[];
	withState: (
		state: unknown,
		config: CloudConfig,
	) => import('../../../../../common/constants/filters/select-filter/types').SelectFilterMonad<'positionEmploymentType'>;
	getInitialState: (
		config: CloudConfig,
	) => import('../../../../../common/constants/filters/select-filter/types').SelectFilterMonad<'positionEmploymentType'>;
	isCompatible: (filterId?: string) => boolean;
	isSupportCustomValue: boolean;
	isSuggestedFilter?: (primaryProduct?: string) => boolean;
}> = createMultiSelectFilter({
	id: 'positionEmploymentType',
	products: productKeys1P,
	alwaysVisible: false,
	universal: false,
	queryParams: {
		key: 'positionEmploymentTypes',
		fetcher: getOptionsWithSelectionsFromQueryParams,
	},
	initialOptions: {
		fetcher: async (config) => {
			return searchPositionEmploymentTypes('', config);
		},
	},
	lookup: {
		fetcher: searchPositionEmploymentTypes,
	},
});
